/**
 * User, role and access-management contracts, shared by the API (route validation) and the web
 * (form validation) so both sides enforce the same rules.
 */
import { z } from "zod";
import { gateLabels, passInManualMode } from "./gate-labels";
import { phone } from "./masters/index";

export const USER_STATUSES = [
  "invited",
  "active",
  "inactive",
  "suspended",
] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const ONBOARDING_SOURCES = ["new", "core"] as const;
export type OnboardingSource = (typeof ONBOARDING_SOURCES)[number];

/** The four status tabs, in the order the old system showed them. */
export const USER_TABS = [
  { key: "invited", label: "Registration pending" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
  { key: "suspended", label: "Suspended" },
] as const;

const email = z.string().trim().toLowerCase().email().max(255);
const fullName = z.string().trim().min(1).max(120);

export const ACCESS_STATES = ["scheduled", "active", "expired"] as const;
export type AccessState = (typeof ACCESS_STATES)[number];

/**
 * Where now sits relative to a user's access window. A null bound is open-ended on that side, so a
 * user with neither bound is always `active`. Shared rather than reimplemented per call site: the
 * row badge, the list filter and the three auth gates must not drift into different ideas of
 * "expired". Boundaries follow the old system exactly -- the window is inclusive of both ends.
 */
export function accessWindowState(
  from: Date | string | null | undefined,
  until: Date | string | null | undefined,
  now: Date = new Date()
): AccessState {
  const t = now.getTime();
  if (from && new Date(from).getTime() > t) return "scheduled";
  if (until && new Date(until).getTime() < t) return "expired";
  return "active";
}

export const isAccessWindowActive = (
  from: Date | string | null | undefined,
  until: Date | string | null | undefined,
  now?: Date
) => accessWindowState(from, until, now) === "active";

/** One end of an access window. Null clears it, leaving that side open-ended. */
const accessBound = z.iso.datetime().nullish();

/** Rejects an inverted window. Applied wherever both bounds arrive together. */
export const accessWindowOrdered = (v: {
  accessActiveFrom?: string | null | undefined;
  accessActiveUntil?: string | null | undefined;
}) =>
  !v.accessActiveFrom ||
  !v.accessActiveUntil ||
  new Date(v.accessActiveFrom) < new Date(v.accessActiveUntil);

/**
 * seedtrac's Opr Code: a department prefix and an operator number, e.g. AR1, PR12, QS2. Stored
 * uppercase so "ar1" and "AR1" are the same code.
 */
export const operatorCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{1,4}\d{1,3}$/, "Use letters then a number, e.g. AR1 or QS2");

/**
 * Invite, rather than create-with-password: an admin never types someone else's password, and the
 * account is not usable until the person proves they hold the mailbox. `core` onboarding carries
 * the VNR Core employee code so the HR record and the login stay joinable.
 */
export const inviteUser = z
  .object({
    source: z.enum(ONBOARDING_SOURCES).default("new"),
    employeeCode: z.string().trim().max(60).optional(),
    operatorCode: operatorCode.nullish(),
    email,
    fullName,
    phone: phone.optional(),
    roleIds: z.array(z.number().int().positive()).default([]),
    /** Every user is mapped to at least one plant; scoping fails closed without one. */
    facilityIds: z
      .array(z.number().int().positive())
      .min(1, "Select at least one plant"),
    /**
     * The plant whose mail account sends this user's invite, login codes and reset codes. Defaults to
     * the only plant; with several plants the admin picks one of them.
     */
    mailFacilityId: z.number().int().positive().nullish(),
    /** Omit both for an account that never expires -- the default an admin gets. */
    accessActiveFrom: accessBound,
    accessActiveUntil: accessBound,
  })
  .refine((v) => v.source !== "core" || !!v.employeeCode, {
    message: "An employee is required when onboarding from VNR Core",
    path: ["employeeCode"],
  })
  .refine(accessWindowOrdered, {
    message: "Access end must be after the access start",
    path: ["accessActiveUntil"],
  });
export type InviteUserInput = z.infer<typeof inviteUser>;

export const updateUser = z.object({
  fullName: fullName.optional(),
  phone: phone.nullish(),
  /** null clears the code. */
  operatorCode: operatorCode.nullish(),
  roleIds: z.array(z.number().int().positive()).optional(),
  facilityIds: z
    .array(z.number().int().positive())
    .min(1, "Select at least one plant")
    .optional(),
  mailFacilityId: z.number().int().positive().nullish(),
  // Deliberately not refined here. A PATCH may send one bound and lean on the stored other one,
  // which a schema cannot see, so `updateUser` in the admin service does the ordering check
  // against the row it has already loaded.
  accessActiveFrom: accessBound,
  accessActiveUntil: accessBound,
  /** Lift the one-sign-in-per-device rule for a shared or support account. */
  allowMultipleSessions: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUser>;

export const suspendUser = z.object({
  reason: z.string().trim().min(1).max(500),
  /** Open-ended when omitted: suspended until someone lifts it. */
  until: z.iso
    .datetime()
    .nullish()
    .refine((v) => !v || new Date(v) > new Date(), {
      message: "The suspension must end in the future",
    }),
});

export const acceptInvite = z.object({
  token: z.string().min(10).max(200),
  // Matches the login form's floor: long enough to matter, no character-class theatre that pushes
  // people to write it on the bin label.
  password: z.string().min(10).max(200),
});

/** Forgot password, step 1: mail a 6-digit code to the account, if there is one. */
export const forgotPassword = z.object({
  email: z.email().max(320),
});

/** Forgot password, step 2: the mailed code and the new password, same floor as an invite. */
export const resetPassword = z.object({
  email: z.email().max(320),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Enter the 6-digit code"),
  password: z.string().min(10).max(200),
});

/**
 * Changing your own password while signed in. The current one is the re-authentication, so it only
 * has to be present; the new one keeps the same floor as an invite or a reset.
 */
export const changePassword = z.object({
  currentPassword: z.string().min(1).max(200),
  password: z.string().min(10).max(200),
});

export const userListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  q: z.string().trim().max(200).optional(),
  status: z.enum(USER_STATUSES).optional(),
  roleId: z.coerce.number().int().positive().optional(),
  facilityId: z.coerce.number().int().positive().optional(),
  source: z.enum(ONBOARDING_SOURCES).optional(),
  /** Filters on the window, not on `status`: an expired user is still `status: 'active'`. */
  access: z.enum(ACCESS_STATES).optional(),
});

export const userResponse = z.object({
  id: z.number().int().positive(),
  email: z.string(),
  fullName: z.string(),
  phone: z.string().nullable(),
  status: z.enum(USER_STATUSES),
  onboardingSource: z.enum(ONBOARDING_SOURCES),
  employeeCode: z.string().nullable(),
  operatorCode: z.string().nullable(),
  suspendedReason: z.string().nullable(),
  suspendedUntil: z.string().nullable(),
  accessActiveFrom: z.string().nullable(),
  accessActiveUntil: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  allowMultipleSessions: z.boolean(),
  roles: z.array(
    z.object({ id: z.number().int().positive(), name: z.string() })
  ),
  facilities: z.array(
    z.object({
      id: z.number().int().positive(),
      plantCode: z.string(),
      name: z.string(),
    })
  ),
  inviteExpiresAt: z.string().nullable(),
  /** The plant whose mail account writes to this user; null = not set, so their mail is refused. */
  mailFacilityId: z.number().int().positive().nullable(),
});

const roleName = z.string().trim().min(1).max(80);

/**
 * The devices a role counts on — `web` is the desk, `native` the handheld. A role for neither would
 * grant nothing anywhere, so at least one is required.
 */
export const ROLE_CLIENTS = ["web", "native"] as const;
export type RoleClient = (typeof ROLE_CLIENTS)[number];
export const roleClients = z
  .array(z.enum(ROLE_CLIENTS))
  .min(1, "Pick at least one device")
  .transform((c) => ROLE_CLIENTS.filter((x) => c.includes(x)));

export const createRole = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[a-z][a-z0-9_]*$/, "lowercase letters, digits and underscores"),
  name: roleName,
  clients: roleClients.optional(),
  /** Start as a copy: the new role gets this role's permissions, form grants and devices. */
  fromRoleId: z.number().int().positive().optional(),
});
export const updateRole = z.object({
  name: roleName.optional(),
  clients: roleClients.optional(),
  /** The Gate module's names for this role (whole-set); {} restores the standard names. */
  gateLabels: gateLabels.optional(),
  /** How holders of Manual Pass In number a new Gate Pass In. */
  passInManualMode: passInManualMode.optional(),
});

/** Whole-set replacement: the matrix posts what is ticked, not a diff. */
export const setRolePermissions = z.object({
  permissions: z.array(z.string().max(120)).max(500),
});

export const formGrant = z.object({
  formCode: z.string().min(1).max(60),
  canView: z.boolean(),
  canCreate: z.boolean(),
  canEdit: z.boolean(),
  canSubmit: z.boolean(),
  canCancel: z.boolean(),
});
export const setRoleFormPermissions = z.object({
  forms: z.array(formGrant).max(200),
});

/**
 * The Roles × Permissions grid's one save: every touched role's full permission list and/or its
 * full form grants, written in one transaction so no role is ever left half-saved.
 */
export const saveRoleMatrix = z.object({
  roles: z
    .array(
      z.object({
        id: z.number().int().positive(),
        permissions: z.array(z.string().max(120)).max(500).optional(),
        forms: z.array(formGrant).max(200).optional(),
        // The role's own settings, so the Roles editor's one Review saves everything together.
        name: roleName.optional(),
        clients: roleClients.optional(),
        gateLabels: gateLabels.optional(),
        passInManualMode: passInManualMode.optional(),
      })
    )
    .min(1)
    .max(100),
});

/** The People × Roles grid's one save: roles granted and removed per person, in one transaction. */
export const setUserRoles = z.object({
  changes: z
    .array(
      z.object({
        userId: z.number().int().positive(),
        add: z.array(z.number().int().positive()).max(100),
        remove: z.array(z.number().int().positive()).max(100),
      })
    )
    .min(1)
    .max(500),
});

export const roleResponse = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  name: z.string(),
  isSystem: z.boolean(),
  clients: z.array(z.enum(ROLE_CLIENTS)),
  gateLabels: z.record(z.string(), z.string()),
  gateTabs: z.array(z.string()),
  passInManualMode: passInManualMode,
  userCount: z.number(),
  permissions: z.array(z.string()),
  forms: z.array(formGrant),
});

export const rbacNodeResponse = z.object({
  id: z.number().int().positive(),
  parentId: z.number().int().positive().nullable(),
  kind: z.enum(["module", "submodule", "action"]),
  code: z.string(),
  label: z.string(),
  sort: z.number(),
  permission: z.string().nullable(),
  isEnabled: z.boolean(),
});

/** Presentation only. `permission`, `code` and `kind` are the seeder's, never the admin's. */
export const updateRbacNode = z
  .object({
    label: z.string().trim().min(1).max(120).optional(),
    sort: z.number().int().min(0).max(9999).optional(),
    isEnabled: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: "Nothing to change",
  });

// ------------------------------------------------------------------ person access (2026-10-05)

/** One personal grant: a code held outside the person's roles, with why and until when. */
export const userOverride = z.object({
  permission: z.string().min(1).max(120),
  reason: z.string().trim().min(3).max(300),
  expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export type UserOverrideInput = z.infer<typeof userOverride>;

/** The Person access screen's save: the person's whole set of personal grants, replaced. */
export const setUserOverrides = z.object({
  overrides: z.array(userOverride).max(200),
});

/**
 * Everything one person can do, with where each permission comes from. `from.roles` are the role
 * ids granting it (on any device); `from.override` is a live personal grant.
 */
export const userAccessResponse = z.object({
  user: userResponse,
  roles: z.array(
    z.object({
      id: z.number().int().positive(),
      name: z.string(),
      clients: z.array(z.enum(ROLE_CLIENTS)),
      userCount: z.number(),
    })
  ),
  permissions: z.array(
    z.object({
      code: z.string(),
      from: z.object({
        roles: z.array(z.number().int().positive()),
        override: z.boolean(),
      }),
    })
  ),
  overrides: z.array(
    z.object({
      permission: z.string(),
      reason: z.string(),
      expiresAt: z.string().nullable(),
      grantedAt: z.string(),
      grantedBy: z.string().nullable(),
      live: z.boolean(),
    })
  ),
  forms: z.array(formGrant.extend({ roleId: z.number().int().positive() })),
});
export type UserAccess = z.infer<typeof userAccessResponse>;

/**
 * What the Roles overview needs beyond the role list. `roleSets` are the distinct combinations of
 * roles people hold, with how many hold each, so "people reached" can be counted without double
 * counting someone in two roles. `lastChanged` is per permission or form code, newest first.
 */
export const rbacSummaryResponse = z.object({
  roleSets: z.array(
    z.object({ roleIds: z.array(z.number().int().positive()), n: z.number() })
  ),
  overrides: z.array(
    z.object({ userId: z.number().int().positive(), permission: z.string() })
  ),
  rolePlants: z.array(
    z.object({
      roleId: z.number().int().positive(),
      plants: z.array(
        z.object({ plantCode: z.string(), name: z.string(), n: z.number() })
      ),
    })
  ),
  lastChanged: z.array(
    z.object({
      kind: z.enum(["permission", "form"]),
      code: z.string(),
      at: z.string(),
    })
  ),
});
export type RbacSummary = z.infer<typeof rbacSummaryResponse>;
